import { component } from "picoapp";

// Reviews published strictly before this day (YYYY-MM-DD) get the badge.
// tbsp-yotpo-widget.liquid mounts this component only on the products it applies to.
const INCENTIVIZED_REVIEW_CUTOFF = "2026-10-27";

const BADGE_CLASS = "op-incentivized-badge";
const observerConfig: MutationObserverInit = {
  childList: true,
  subtree: true,
  characterData: true,
  attributes: true,
  attributeFilter: ["data-id"],
};

const INFO_ICON = `<svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="7.25" stroke="currentColor" stroke-width="1.5"/><path d="M8 7v4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="8" cy="4.75" r="1" fill="currentColor"/></svg>`;

// Converts Yotpo's MM/DD/YY text to YYYY-MM-DD so it compares as a string.
export const parseReviewDate = (text: string): string | null => {
  const match = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/(\d{2})$/.exec(
    text.trim()
  );
  if (!match) return null;

  const [, month = "", day = "", year = ""] = match;
  return `20${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

export const isBeforeCutoff = (dateText: string, cutoff: string): boolean => {
  const date = parseReviewDate(dateText);
  return date !== null && date < cutoff;
};

const createBadge = (reviewId: string, label: string, tooltipText: string) => {
  const tooltipId = `${BADGE_CLASS}-tooltip-${reviewId}`;
  const badge = document.createElement("div");
  badge.className = BADGE_CLASS;
  badge.dataset.reviewId = reviewId;
  badge.innerHTML = `
    <button type="button" class="${BADGE_CLASS}__button" aria-describedby="${tooltipId}">
      ${INFO_ICON}<span class="${BADGE_CLASS}__label"></span>
    </button>
    <span id="${tooltipId}" class="${BADGE_CLASS}__tooltip" role="tooltip"></span>`;

  // Copy comes from translations, so it is set as text, never as HTML.
  badge.querySelector(`.${BADGE_CLASS}__label`)!.textContent = label;
  badge.querySelector(`.${BADGE_CLASS}__tooltip`)!.textContent = tooltipText;
  return badge;
};

const updateBadges = (node: HTMLElement) => {
  const { incentivizedLabel = "", incentivizedTooltip = "" } = node.dataset;

  node.querySelectorAll<HTMLElement>(".yotpo-review").forEach((review) => {
    const reviewId = review.dataset.id;
    const existing = review.querySelector<HTMLElement>(`.${BADGE_CLASS}`);
    const dateText =
      review.querySelector(".yotpo-date-format")?.textContent ?? "";
    const qualifies = isBeforeCutoff(dateText, INCENTIVIZED_REVIEW_CUTOFF);

    if (qualifies && existing && existing.dataset.reviewId === reviewId) return;
    existing?.remove();

    const leftPanel = review.querySelector(".yotpo-review-left-panel");
    if (!qualifies || !reviewId || !leftPanel) return;

    const badge = createBadge(reviewId, incentivizedLabel, incentivizedTooltip);
    const reviewer = leftPanel.querySelector(".yotpo-reviewer");
    if (reviewer) reviewer.after(badge);
    else leftPanel.append(badge);
  });
};

const badgeFrom = (target: EventTarget | null) =>
  target instanceof Element
    ? target.closest<HTMLElement>(`.${BADGE_CLASS}`)
    : null;

const containsTarget = (badge: HTMLElement, target: EventTarget | null) =>
  target instanceof Node && badge.contains(target);

const closeAll = (node: HTMLElement, except?: HTMLElement | null) => {
  node
    .querySelectorAll<HTMLElement>(`.${BADGE_CLASS}[data-open]`)
    .forEach((badge) => {
      if (badge !== except) badge.removeAttribute("data-open");
    });
};

const mountIncentivizedReviews = (node: HTMLElement): (() => void) => {
  // iOS Safari reports taps as mouse clicks, so the input type comes from pointerdown.
  let lastPointerType = "";
  const onPointerDown = (event: PointerEvent) => {
    lastPointerType = event.pointerType;
    closeAll(node, badgeFrom(event.target));
  };

  // CSS opens the tooltip on mouse hover and keyboard focus; taps toggle it here.
  // Keyboard-triggered clicks have a detail of 0.
  const onClick = (event: MouseEvent) => {
    const badge = badgeFrom(event.target);
    if (!badge || event.detail === 0 || lastPointerType === "mouse") return;
    badge.toggleAttribute("data-open");
  };

  const onLeave = (event: PointerEvent | FocusEvent) => {
    if ("pointerType" in event && event.pointerType !== "mouse") return;
    const badge = badgeFrom(event.target);
    if (badge && !containsTarget(badge, event.relatedTarget)) {
      badge.removeAttribute("data-open");
      delete badge.dataset.dismissed;
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;
    closeAll(node);
    [
      badgeFrom(document.activeElement),
      ...node.querySelectorAll<HTMLElement>(`.${BADGE_CLASS}:hover`),
    ].forEach((badge) => {
      if (badge) badge.dataset.dismissed = "true";
    });
  };

  updateBadges(node);
  const observer = new MutationObserver(() => updateBadges(node));
  observer.observe(node, observerConfig);

  const listeners = new AbortController();
  const { signal } = listeners;
  node.addEventListener("pointerout", onLeave, { signal });
  node.addEventListener("focusout", onLeave, { signal });
  node.addEventListener("click", onClick, { signal });
  document.addEventListener("pointerdown", onPointerDown, { signal });
  document.addEventListener("keydown", onKeyDown, { signal });

  return () => {
    observer.disconnect();
    listeners.abort();
  };
};

export const yotpoIncentivizedReviews = component(mountIncentivizedReviews);
